import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { AlertTriangle, Sparkles, ArrowRight, Loader2, Mail } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { billingApi, stripeApi } from '../../services/api';
import { toast } from 'sonner';
import { saveOperationState } from '../../hooks/useOperationState';
import { useAuth } from '../../contexts/AuthContext';

const FeatureLimitModal = ({
  isOpen,
  onClose,
  usage,  // { plan_tier, limit, used, remaining }
  newFeaturesCount,  // Number of features user is trying to add
  epics = [],  // The extracted epics with their features
  onRemoveFeatures,  // Callback when user chooses to remove features
  wizardState = null,  // Optional: state to preserve for resume
  returnPath = '/products/new',  // Path to return to after upgrade
  onUpgradeComplete,  // Callback when upgrade is complete
}) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const { organization } = useAuth();
  
  const [isLoading, setIsLoading] = useState(false);
  const [upgradeContext, setUpgradeContext] = useState(null);
  const [contextLoading, setContextLoading] = useState(true);
  const [requestMessage, setRequestMessage] = useState('');
  
  const featuresToRemove = newFeaturesCount - (usage?.remaining || 0);
  
  // Fetch upgrade context when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchUpgradeContext();
    }
  }, [isOpen]);
  
  const fetchUpgradeContext = async () => {
    setContextLoading(true);
    try {
      const response = await billingApi.getUpgradeContext('features');
      setUpgradeContext(response.data);
    } catch (error) {
      console.error('Failed to fetch upgrade context:', error);
    } finally {
      setContextLoading(false);
    }
  };
  
  const handleUpgrade = async () => {
    setIsLoading(true);
    
    try {
      // Check if user can upgrade directly (owner/billing admin)
      if (upgradeContext?.can_upgrade) {
        if (upgradeContext.has_payment_method) {
          // One-click upgrade
          const plan = upgradeContext.upgrade_options?.[0];
          if (plan) {
            const response = await billingApi.oneClickUpgrade(plan.plan_id);
            if (response.data?.success) {
              toast.success(`Upgraded to ${plan.label}! 🎉`);
              onUpgradeComplete?.();
              onClose();
              return;
            }
          }
        }
        
        // Redirect to Stripe Checkout
        if (wizardState) {
          saveOperationState('feature_creation', wizardState, returnPath, 'upgrade');
        }
        
        // Get plan_id - use upgrade option or current org plan
        const planId = upgradeContext.upgrade_options?.[0]?.plan_id || organization?.plan_id;
        if (!planId) {
          toast.error('No plan found. Please contact support.');
          setIsLoading(false);
          return;
        }
        
        const baseUrl = window.location.origin;
        const successUrl = `${baseUrl}${returnPath}?resume=true&checkout=success`;
        const cancelUrl = `${baseUrl}${returnPath}?resume=true&checkout=cancelled`;
        
        const response = await stripeApi.createCheckoutSession({
          seats: 1,
          plan_id: planId,
          success_url: successUrl,
          cancel_url: cancelUrl
        });
        if (response.data?.checkout_url) {
          window.location.href = response.data.checkout_url;
          return;
        }
      }
      
      toast.error('Unable to process upgrade. Please contact support.');
    } catch (error) {
      console.error('Upgrade error:', error);
      toast.error('Failed to process upgrade');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle PM/Member request upgrade
  const handleRequestUpgrade = async () => {
    setIsLoading(true);
    
    try {
      await billingApi.requestUpgrade('features', requestMessage || null);
      toast.success('Upgrade request sent! 📧', {
        description: `${upgradeContext?.owner_name || 'The organization owner'} has been notified.`,
      });
      onClose();
    } catch (error) {
      console.error('Request upgrade error:', error);
      toast.error('Failed to send upgrade request');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Determine if user can upgrade or only request
  const canUpgrade = upgradeContext?.can_upgrade;
  const canRequest = upgradeContext?.can_request_upgrade;
  const priceDisplay = upgradeContext?.upgrade_options?.[0]?.price_display;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={cn("max-w-xl", colors.bgCard, colors.borderPrimary)}>
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <DialogTitle className={cn("text-xl", colors.textPrimary)}>
                You&apos;ve Hit Your Free Tier Limit
              </DialogTitle>
              <DialogDescription className={cn("text-sm mt-1", colors.textSecondary)}>
                Your AI extraction generated more features than your free tier allows.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        
        <div className="py-4 space-y-5">
          {/* The Impact Summary */}
          <div className="grid grid-cols-3 gap-3">
            <div className={cn("p-3 rounded-lg text-center", colors.bgTertiary)}>
              <div className={cn("text-2xl font-bold", colors.textPrimary)}>{newFeaturesCount}</div>
              <div className={cn("text-xs", colors.textMuted)}>Generated</div>
            </div>
            <div className="p-3 rounded-lg text-center bg-green-50 dark:bg-green-900/20">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">{usage?.remaining || 0}</div>
              <div className="text-xs text-green-600 dark:text-green-400">Can Keep</div>
            </div>
            <div className="p-3 rounded-lg text-center bg-red-50 dark:bg-red-900/20">
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">{Math.max(0, featuresToRemove)}</div>
              <div className="text-xs text-red-600 dark:text-red-400">Will Lose</div>
            </div>
          </div>
          
          {/* Options */}
          <div className="space-y-3">
            <h4 className={cn("text-sm font-medium", colors.textPrimary)}>Choose How to Proceed:</h4>
            
            {contextLoading ? (
              // Loading state
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
              </div>
            ) : canUpgrade ? (
              // Owner/Billing Admin - Can upgrade directly
              <button
                onClick={handleUpgrade}
                disabled={isLoading}
                className={cn(
                  "w-full p-4 rounded-lg border-2 text-left transition-all hover:shadow-lg",
                  "bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20",
                  "border-purple-400 dark:border-purple-600 hover:border-purple-500 dark:hover:border-purple-400",
                  isLoading && "opacity-70 cursor-not-allowed"
                )}
              >
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center shrink-0">
                    {isLoading ? (
                      <Loader2 className="w-6 h-6 text-purple-600 animate-spin" />
                    ) : (
                      <Sparkles className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-purple-700 dark:text-purple-300 text-lg">
                      {isLoading ? 'Processing...' : 'Upgrade to Pro - Keep Everything'}
                    </p>
                    <p className="text-sm mt-1 text-purple-600/80 dark:text-purple-400/80">
                      Unlimited features, priority AI processing, advanced analytics, and team collaboration. 
                      <span className="font-medium"> Your progress will be saved.</span>
                    </p>
                    {priceDisplay && (
                      <div className="mt-2 flex items-center gap-2 text-sm text-purple-600 dark:text-purple-400">
                        <span className="font-semibold">{priceDisplay}</span>
                        <span className="text-purple-400">•</span>
                        <span>Cancel anytime</span>
                      </div>
                    )}
                  </div>
                  {!isLoading && <ArrowRight className="w-5 h-5 mt-3 shrink-0 text-purple-500" />}
                </div>
              </button>
            ) : canRequest ? (
              // PM/Member - Request upgrade from owner
              <div className="space-y-4">
                <div className={cn("p-4 rounded-lg border", colors.bgTertiary, "border-blue-200 dark:border-blue-800")}>
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-blue-500 mt-0.5" />
                    <div>
                      <p className={cn("font-medium", colors.textPrimary)}>
                        Request Upgrade from Admin
                      </p>
                      <p className={cn("text-sm mt-1", colors.textSecondary)}>
                        Your organization owner ({upgradeContext?.owner_name || upgradeContext?.owner_email || 'admin'}) 
                        will be notified via email and in-app.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className={cn("text-sm font-medium", colors.textSecondary)}>
                    Add a message (optional)
                  </label>
                  <textarea
                    value={requestMessage}
                    onChange={(e) => setRequestMessage(e.target.value)}
                    placeholder="I need more features to complete the product setup..."
                    className={cn(
                      "w-full p-3 rounded-lg border text-sm resize-none",
                      colors.bgTertiary, colors.borderPrimary, colors.textPrimary
                    )}
                    rows={2}
                  />
                </div>
                
                <Button
                  onClick={handleRequestUpgrade}
                  disabled={isLoading}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Mail className="w-4 h-4 mr-2" />
                  )}
                  Request Upgrade
                </Button>
              </div>
            ) : null}
            
            {/* Info box with remove option */}
            <div className={cn("p-4 rounded-lg border", colors.bgTertiary, "border-gray-300 dark:border-gray-600")}>
              <p className={cn("text-sm text-center", colors.textSecondary)}>
                You&apos;ve used all <strong>{usage?.limit}</strong> features on your free tier. 
                {canUpgrade ? ' Upgrade to Pro to continue creating products with features.' : ' Contact your admin to upgrade.'}
              </p>
              {featuresToRemove > 0 && (
                <>
                  <div className={cn("text-center text-xs font-medium my-3", colors.textMuted)}>OR</div>
                  <button
                    onClick={onRemoveFeatures}
                    disabled={isLoading}
                    className={cn(
                      "w-full text-center text-sm font-medium py-2 px-4 rounded-md transition-colors",
                      "text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700",
                      isLoading && "opacity-50 cursor-not-allowed"
                    )}
                  >
                    You can remove {featuresToRemove} feature{featuresToRemove !== 1 ? 's' : ''} to continue saving this product
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
        
        <DialogFooter className="pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button 
            variant="outline" 
            onClick={onClose} 
            disabled={isLoading}
            className={cn("border-gray-300 dark:border-gray-600", colors.textPrimary)}
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FeatureLimitModal;
